// Scène greybox pilotée par données : une seule classe, instanciée par lieu depuis un LocationConfig.
// Étend GameScene et n'override que deux coutures (loadMapImages no-op + createMap runtime).
// Tout le reste (sprite joueur, déplacement GridEngine, caméra, Door, PNJ DiceBear, interactions)
// est hérité tel quel.
import 'phaser'
import { CollisionStrategy } from 'grid-engine'
import GameScene from '../GameScene'
import { Door } from '../components/Door'
import { preloadLocationPortraits, spawnCharactersForLocation } from '../components/CharacterFactory'
import { TILE_SIZE } from './greyboxTileset'
import { getTheme, MapTheme } from './themes'
import type { LocationConfig } from './campusMaps'
import GlobalInfo from '../../GlobalInfo'
import ServerLink from '../../net/ServerLink'
import { RemoteAgentDriver } from '../../net/RemoteAgentDriver'
import { DynamicObjectDriver } from '../../net/DynamicObjectDriver'
import type { DynamicObjectPublic } from '../../net/protocol'

const MAP_LAYER = 'greybox'

export default class GreyboxLocationScene extends GameScene {
    protected cfg: LocationConfig // protected so RoomScene can retarget cfg.id to the room being viewed
    private theme: MapTheme
    private remoteDriver?: RemoteAgentDriver
    private dynObjDriver?: DynamicObjectDriver
    private dynamicSprites = new Map<string, Phaser.GameObjects.Image>()
    private dynamicDecor = new Map<string, Phaser.GameObjects.Image>()

    constructor(cfg: LocationConfig) {
        super(cfg.id, cfg.spawn.x, cfg.spawn.y, [])
        this.cfg = cfg
        this.theme = getTheme(cfg.theme) // 'greybox' par défaut, 'tinyTown' pour le campus
        // imageNames.Map / Perli ne sert qu'à dériver des clés (jamais à charger un JSON ici).
        this.imageNames = { Perli: `${cfg.id}_perli`, Map: cfg.id }
        this.gridEngineSettings.characterCollisionStrategy = CollisionStrategy.BLOCK_ONE_TILE_AHEAD
    }

    // Requis (abstrait dans GameScene) — pas d'images d'objets ici.
    loadObjectImages(): void { /* rien */ }

    preload(): void {
        super.preload()               // door graphic + sons communs
        super.loadAvatarSpritesheet() // sprite joueur (Perli, placeholder)
        this.loadMapImages()          // override no-op (pas de tilemap JSON)
        this.theme.preload(this)      // charge la feuille du thème (Tiny Town) si besoin
        getTheme('indoors').preload(this) // feuille 'rlsheet' pour les props créés par les agents (tous lieux)
        if (this.cfg.kazis && Object.keys(this.cfg.kazis).length > 0) {
            preloadLocationPortraits(this, this.cfg.id) // portraits DiceBear des Kazis du lieu
        }
    }

    // --- Override moteur : carte construite au runtime depuis l'ASCII + le thème ---

    loadMapImages(): void { /* no-op : pas de tilemap Tiled */ }

    createMap(): void {
        this.theme.ensure(this) // génère la texture greybox si besoin

        const rows = this.cfg.ascii
        const width = Math.max(...rows.map(r => r.length))
        const grid: string[][] = rows.map(r => Array.from(r.padEnd(width, '.')))
        const height = grid.length

        this.map = this.make.tilemap({ tileWidth: TILE_SIZE, tileHeight: TILE_SIZE, width, height })
        const tileset = this.map.addTilesetImage(
            this.theme.textureKey, this.theme.textureKey, TILE_SIZE, TILE_SIZE, 0, this.theme.spacing,
        )
        const layer = this.map.createBlankLayer(MAP_LAYER, tileset!)!

        for (let y = 0; y < height; y++) {
            for (let x = 0; x < width; x++) {
                const { index, solid, overlay } = this.theme.resolve(grid, x, y)
                const tile = layer.putTileAt(index, x, y)
                // GridEngine lit ge_collide sur la tuile vivante (résolue par nom de calque).
                if (tile && solid) tile.properties = { ge_collide: true }
                // meuble / arbre sur fond transparent : dessiné par-dessus le sol/l'herbe.
                // Créé avant le joueur (initiateGridEngine) -> rendu sous le joueur et les Kazis.
                if (overlay !== undefined) {
                    this.add.image(x * TILE_SIZE + TILE_SIZE / 2, y * TILE_SIZE + TILE_SIZE / 2,
                        this.theme.textureKey, overlay)
                }
            }
        }
    }

    createNpcs(): void {
        // Server-authoritative : les Kazis (agents IA) sont pilotés par le serveur de monde,
        // pas par des placements statiques. On crée un driver qui les fait apparaître/marcher
        // depuis les snapshots du serveur. Repli statique si le serveur n'est pas branché.
        if (ServerLink.enabled) {
            this.remoteDriver = new RemoteAgentDriver(this, this.cfg.id)
            this.dynObjDriver = new DynamicObjectDriver(this, this.cfg.id)
            ServerLink.enterLocation(this.cfg.id)
            return
        }
        if (!this.cfg.kazis) return
        spawnCharactersForLocation(this, {
            locationId: this.cfg.id,
            placements: this.cfg.kazis,
        })
    }

    // --- Agent-created props (rendered at runtime from server messages; see DynamicObjectDriver) ---

    /** Draw a server-spawned prop + register it as a tappable interactable (+ collision if solid). Also
     *  handles RE-spawns (the server re-broadcasts on level-up / co-build): updates the name + decoration
     *  without recreating the base sprite. */
    spawnDynamicObject(obj: DynamicObjectPublic): void {
        if (obj.location !== this.cfg.id) return
        // Always draw from the 'rlsheet' (indoors) furniture sheet — its frame indices are stable
        // regardless of the room's own theme texture (campus/greybox use different sheets).
        const RL = getTheme('indoors').textureKey
        const px = obj.tile.x * TILE_SIZE + TILE_SIZE / 2
        const py = obj.tile.y * TILE_SIZE + TILE_SIZE / 2
        const display = obj.name || obj.label
        if (!this.dynamicSprites.has(obj.objectId)) {
            const img = this.add.image(px, py, RL, obj.spriteFrame).setDepth(py)
            this.dynamicSprites.set(obj.objectId, img)
        }
        // Level-up cosmetic: a distinct emblem per tier drawn above the base sprite. UPDATE the frame on a
        // re-spawn so a prop that crosses a NEW tier visibly changes (each tier has its own decorFrame now).
        if (obj.decorFrame !== undefined) {
            const existing = this.dynamicDecor.get(obj.objectId)
            if (existing) existing.setFrame(obj.decorFrame)
            else this.dynamicDecor.set(obj.objectId, this.add.image(px, py - TILE_SIZE * 0.5, RL, obj.decorFrame).setDepth(py + 1))
        }
        // tappable like a static prop (GameScene.doInteraction reads interactablesByTile) — keep the
        // authored name + latest effects fresh on every (re)spawn.
        const ireg: Record<string, unknown> = (this as any).interactablesByTile ?? ((this as any).interactablesByTile = {})
        ireg[`${obj.tile.x},${obj.tile.y}`] = { x: obj.tile.x, y: obj.tile.y, label: display, effects: obj.effects, objectId: obj.objectId, blurb: obj.blurb, coCreators: obj.coCreators }
        if (obj.solid && this.map) {
            const t = this.map.getTileAt(obj.tile.x, obj.tile.y, false, MAP_LAYER)
            if (t) t.properties = { ...(t.properties ?? {}), ge_collide: true }
        }
    }

    /** Remove a decayed / cleared prop: destroy the sprite, drop its interactable + collision. */
    removeDynamicObject(objectId: string): void {
        const img = this.dynamicSprites.get(objectId)
        if (!img) return
        const ireg: Record<string, any> = (this as any).interactablesByTile ?? {}
        for (const k of Object.keys(ireg)) {
            if (ireg[k]?.objectId === objectId) {
                const [x, y] = k.split(',').map(Number)
                const t = this.map?.getTileAt(x, y, false, MAP_LAYER)
                if (t?.properties) delete (t.properties as any).ge_collide
                delete ireg[k]
            }
        }
        img.destroy()
        this.dynamicSprites.delete(objectId)
        this.dynamicDecor.get(objectId)?.destroy()
        this.dynamicDecor.delete(objectId)
    }

    create(): void {
        super.create()
        // Portes (placées aux tuiles 'D' dessinées dans l'ASCII).
        this.cfg.doors.forEach(d =>
            new Door({ scene: this, xPosition: d.x, yPosition: d.y, nextScene: d.to })
        )
        this.createLabels()
        if (this.cfg.banner) this.createBanner(this.cfg.banner)
        // Registre tuile -> prop interactif (lu par GameScene.pointerdown, comme kaziByTile).
        const ireg: Record<string, unknown> = ((this as any).interactablesByTile = {})
        this.cfg.interactions?.forEach(it => { ireg[`${it.x},${it.y}`] = it })

        // scene.switch endort/réveille les scènes : on (re)signale le lieu au serveur au réveil.
        this.events.on(Phaser.Scenes.Events.WAKE, () => {
            if (ServerLink.enabled) ServerLink.enterLocation(this.cfg.id)
        })
        // Bannière "beat" émergent (ex : un élève propose un goûter) pour ce lieu.
        GlobalInfo.on('server-event-beat', this.onBeat, this)
        this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => GlobalInfo.off('server-event-beat', this.onBeat, this))
    }

    private onBeat = (p: { title: string; location: string; description: string }): void => {
        if (p.location !== this.cfg.id) return
        const W = this.scale.width
        const banner = this.add.text(W / 2, 14, `✦ ${p.title}\n${p.description}`, {
            fontFamily: 'sans-serif', fontSize: '13px', fontStyle: 'bold', color: '#10151f',
            backgroundColor: '#f6e05ee8', padding: { x: 10, y: 7 }, align: 'center', wordWrap: { width: W - 40 },
        }).setOrigin(0.5, 0).setScrollFactor(0).setDepth(2000).setResolution(3)
        this.tweens.add({ targets: banner, alpha: 0, delay: 3500, duration: 1200, onComplete: () => banner.destroy() })
    }

    // Bannière "coming soon" fixe au centre de l'écran (overlay, contre le zoom caméra via scrollFactor 0).
    // Utilisée par la carte "Vacances" (en développement) ; re-centrée au redimensionnement.
    // Ajoute un compte à rebours "reprise des cours" quand le serveur annonce la prochaine ouverture
    // (clock.nextSchoolOpenMs — week-ends et vacances déjà pris en compte côté serveur).
    private createBanner(text: string): void {
        const draw = (): void => {
            const W = this.scale.width, H = this.scale.height
            banner.setPosition(W / 2, H / 2).setWordWrapWidth(Math.min(360, W - 40))
        }
        const banner = this.add.text(0, 0, text, {
            fontFamily: 'sans-serif', fontSize: '16px', fontStyle: 'bold', color: '#f7fafc',
            backgroundColor: '#10151fe6', padding: { x: 16, y: 12 }, align: 'center',
        }).setOrigin(0.5).setScrollFactor(0).setDepth(3000).setResolution(3)
        const countdown = (): void => {
            const openMs = ServerLink.clock?.nextSchoolOpenMs
            const now = ServerLink.gameNow()
            const left = openMs != null && now != null ? openMs - now : 0
            banner.setText(left > 0 ? `${text}\n\n🔔 Reprise des cours dans ${fmtCountdown(left)}\n${fmtOpenLabel(openMs!)}` : text)
            draw()
        }
        countdown()
        this.time.addEvent({ delay: 1000, loop: true, callback: countdown })
        this.scale.on('resize', draw, this)
        this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.scale.off('resize', draw, this))
    }

    // Étiquettes (cadre + nom) posées sur la carte : le joueur reconnaît son collège.
    // Style par type : 'building' (titre), 'zone' (cour/parking/terrain), 'door' (petit, au-dessus d'une porte).
    // Le texte est en coords MONDE (ancré aux bâtiments) mais contre-scalé par le zoom caméra, pour
    // garder une taille à l'écran CONSTANTE et lisible quel que soit le zoom (cf. scaleLabels).
    private labelObjs: Phaser.GameObjects.Text[] = []

    private createLabels(): void {
        if (!this.cfg.labels) return
        const STYLES: Record<string, Phaser.Types.GameObjects.Text.TextStyle> = {
            building: { fontFamily: 'sans-serif', fontStyle: 'bold', fontSize: '13px', color: '#ffffff', backgroundColor: '#1a202cdd', padding: { x: 5, y: 3 }, align: 'center' },
            zone: { fontFamily: 'sans-serif', fontSize: '12px', color: '#e2e8f0', backgroundColor: '#2b4c7eaa', padding: { x: 5, y: 2 }, align: 'center' },
            door: { fontFamily: 'sans-serif', fontStyle: 'bold', fontSize: '10px', color: '#10151f', backgroundColor: '#f6e05ee0', padding: { x: 3, y: 1 }, align: 'center' },
        }
        for (const l of this.cfg.labels) {
            const t = this.add.text(
                l.x * TILE_SIZE + TILE_SIZE / 2,
                l.y * TILE_SIZE + TILE_SIZE / 2,
                l.text,
                STYLES[l.kind ?? 'building'],
            ).setOrigin(0.5).setDepth(900).setResolution(3)
            this.labelObjs.push(t)
        }
        this.scaleLabels()
        this.scale.on('resize', this.scaleLabels, this)
    }

    // Contre-scale les étiquettes par le zoom caméra -> taille écran constante (police = px écran).
    private scaleLabels(): void {
        const z = this.cameras.main.zoom || 1
        for (const t of this.labelObjs) t.setScale(1 / z)
    }
}

// ── compte à rebours "reprise des cours" ─────────────────────────────────────

/** "2 j 14 h", "1 h 37 min", "12 min 05 s" — l'unité de tête décide de la granularité. */
function fmtCountdown(ms: number): string {
    const totalS = Math.max(0, Math.floor(ms / 1000))
    const d = Math.floor(totalS / 86_400), h = Math.floor((totalS % 86_400) / 3600)
    const min = Math.floor((totalS % 3600) / 60), s = totalS % 60
    if (d > 0) return `${d} j ${h} h`
    if (h > 0) return `${h} h ${String(min).padStart(2, '0')} min`
    return `${min} min ${String(s).padStart(2, '0')} s`
}

/** "(jeudi 08:00)" — le jour/heure de la cloche, dans le fuseau du jeu (Paris). */
const OPEN_FMT = new Intl.DateTimeFormat('fr-FR', { timeZone: 'Europe/Paris', weekday: 'long', hour: '2-digit', minute: '2-digit' })
function fmtOpenLabel(gameMs: number): string {
    return `(${OPEN_FMT.format(gameMs).replace(' à ', ' ')})`
}
